/** 与后端 ClientAddrParser 对齐：结构化 JSON 或遗留「地址 姓名 电话」纯文本 */

export type ClientAddrFields = {
  name?: string;
  companyName?: string;
  email?: string;
  tel?: string;
  addr?: string;
  city?: string;
  state?: string;
  zip?: string;
};

const TRAILING_PHONE = /^(.*?)\s+(\+?[\d\-\s()]{6,}\d)\s*$/;

function looksLikePhone(s: string): boolean {
  let digits = s.replace(/[\s\-()]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  return /^\d{7,15}$/.test(digits);
}

function normalizePhone(raw: string): string {
  return raw.trim().replace(/[\s()]/g, '');
}

export function parseClientAddr(clientAddr?: string | null): ClientAddrFields {
  if (!clientAddr || !clientAddr.trim()) return {};
  const raw = clientAddr.trim();
  if (raw.startsWith('{')) {
    try {
      const node = JSON.parse(raw) as Record<string, unknown>;
      const pick = (...keys: string[]) => {
        for (const k of keys) {
          const v = node[k];
          if (typeof v === 'string' && v.trim()) return v.trim();
        }
        return undefined;
      };
      return {
        name: pick('name', 'reName', 'consignee', 'contact'),
        companyName: pick('companyName', 'company', 'corpName'),
        email: pick('email', 'mail'),
        tel: pick('tel', 'phone', 'reTel', 'mobile'),
        addr: pick('addr', 'address', 'reAddr'),
        city: pick('city', 'reCity'),
        state: pick('state', 'province', 'reState'),
        zip: pick('zip', 'postcode', 'reZip'),
      };
    } catch {
      // fall through
    }
  }
  const m = TRAILING_PHONE.exec(raw);
  if (m) {
    const phone = normalizePhone(m[2]);
    if (looksLikePhone(phone)) {
      const before = m[1].trim();
      return { tel: phone, addr: before || raw };
    }
  }
  return { addr: raw };
}

/** 列表/详情展示用 */
export function formatClientAddr(clientAddr?: string | null): string {
  if (!clientAddr || !clientAddr.trim()) return '-';
  const p = parseClientAddr(clientAddr);
  if (clientAddr.trim().startsWith('{')) {
    const parts = [p.companyName, p.name, p.email, p.addr, p.tel].filter(Boolean);
    return parts.length ? parts.join(' | ') : clientAddr;
  }
  return clientAddr;
}

/** 预申报：将客户地址簿条目存为结构化 JSON，供哲盟 Q5 等解析 */
export function buildStructuredClientAddr(addr: {
  contactName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address?: string;
}): string {
  const obj: Record<string, string> = {};
  if (addr.contactName?.trim()) obj.name = addr.contactName.trim();
  if (addr.companyName?.trim()) obj.companyName = addr.companyName.trim();
  if (addr.email?.trim()) obj.email = addr.email.trim();
  if (addr.phone?.trim()) obj.tel = addr.phone.trim();
  if (addr.address?.trim()) obj.addr = addr.address.trim();
  return JSON.stringify(obj);
}

const Q5_IDENTIFIERS = new Set(['HST', 'LMT', 'KLD']);

export function isIntelinkQ5Identifier(identifier?: string | null): boolean {
  return !!identifier && Q5_IDENTIFIERS.has(identifier.trim().toUpperCase());
}

/** 交 Q5 代理仓前：检查能否拼出 reName/reTel/reAddr（国家由线路提供） */
export function missingQ5ConsigneeFields(
  clientAddr: string | undefined | null,
  clientCode?: string | null
): string[] {
  const p = parseClientAddr(clientAddr);
  const missing: string[] = [];
  const name = p.name || (clientCode && clientCode.trim()) || undefined;
  const tel = p.tel;
  const addr = p.addr || (clientAddr && clientAddr.trim()) || undefined;
  if (!name) missing.push('收件人姓名(reName)');
  if (!tel) missing.push('收件电话(reTel)');
  if (!addr) missing.push('收件地址(reAddr)');
  return missing;
}
